class ImageVideo extends HTMLElement {
  constructor() {
    super()
    this.popup = null
    this.popupVideo = null
    this.closeButton = null
    this.backdrop = null
    this.muteButton = null
    this.playPauseButton = null
    this.popupMovedToBody = false
  }

  connectedCallback() {
    this.popup = this.querySelector('.video-popup__container')
    this.popupVideo = this.querySelector('.video-popup__video')
    this.closeButton = this.querySelector('.video-popup__close')
    this.backdrop = this.querySelector('.video-popup__backdrop')
    this.muteButton = this.querySelector('.video-popup__mute-button')
    this.playPauseButton = this.querySelector('.video-popup__play-pause-button')
    
    // Get display mode from data attribute
    this.displayMode = this.getAttribute('data-video-mode') || 'popup'
    
    // Move popup to body to break out of stacking contexts (only for popup mode)
    if (this.popup && !this.popupMovedToBody && this.displayMode === 'popup') {
      document.body.appendChild(this.popup)
      this.popupMovedToBody = true
    }
    
    // Get video item
    const videoItem = this.querySelector('.image-video__video-item')
    
    // Hide play button for autoplaying videos (only in popup mode)
    if (videoItem && this.displayMode === 'popup') {
      const videoElement = videoItem.querySelector('video')
      if (videoElement && videoElement.autoplay) {
        const playButton = videoItem.querySelector('.image-video__video-item-play-button')
        if (playButton) {
          playButton.classList.add('image-video__video-item-play-button--hidden')
        }
      }
    }
    
    // Only attach popup handlers if in popup mode
    if (videoItem && this.displayMode === 'popup') {
      // Add click event to video item
      videoItem.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        
        // Try to get video source from the video element inside
        const videoElement = videoItem.querySelector('video')
        let videoSrc = null
        
        if (videoElement) {
          // Check if video has src attribute
          if (videoElement.src) {
            videoSrc = videoElement.src
          } 
          // Check if video has source element
          else if (videoElement.querySelector('source')) {
            videoSrc = videoElement.querySelector('source').src
          }
          // Fallback to data attribute
          else {
            videoSrc = videoItem.getAttribute('data-video-src')
          }
        } else {
          // Fallback to data attribute if no video element found
          videoSrc = videoItem.getAttribute('data-video-src')
        }
        
        if (videoSrc || videoElement) {
          this.openPopup(videoSrc, videoElement)
        }
      })
      
      // Make video element clickable to open popup
      const videoElement = videoItem.querySelector('video')
      if (videoElement) {
        videoElement.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          
          // Get video source
          let videoSrc = null
          if (videoElement.src) {
            videoSrc = videoElement.src
          } else if (videoElement.querySelector('source')) {
            videoSrc = videoElement.querySelector('source').src
          } else {
            videoSrc = videoItem.getAttribute('data-video-src')
          }
          
          if (videoSrc || videoElement) {
            this.openPopup(videoSrc, videoElement)
          }
        })
        // Ensure video element is clickable
        videoElement.style.pointerEvents = 'auto'
        videoElement.style.cursor = 'pointer'
      }
    }
    
    // Close popup when clicking close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => {
        this.closePopup()
      })
    }
    
    // Close popup when clicking backdrop
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => {
        this.closePopup()
      })
    }
    
    // Close popup when pressing Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup && this.popup.classList.contains('active')) {
        this.closePopup()
      }
    })
    
    // Handle mute/unmute button
    if (this.muteButton && this.popupVideo) {
      this.muteButton.addEventListener('click', () => {
        this.toggleMute()
      })
    }
    
    // Handle play/pause button
    if (this.playPauseButton && this.popupVideo) {
      this.playPauseButton.addEventListener('click', (e) => {
        e.stopPropagation()
        this.togglePlayPause()
      })
      
      // Update button state based on video events
      this.popupVideo.addEventListener('play', () => {
        if (this.playPauseButton) {
          this.playPauseButton.classList.add('playing')
        }
      })
      
      this.popupVideo.addEventListener('pause', () => {
        if (this.playPauseButton) {
          this.playPauseButton.classList.remove('playing')
        }
      })
    }
  }
  
  openPopup(videoSrc, originalVideoElement = null) {
    if (this.popup && this.popupVideo) {
      // Ensure popup is in body to break out of stacking contexts
      if (!this.popupMovedToBody && this.popup.parentElement !== document.body) {
        document.body.appendChild(this.popup)
        this.popupMovedToBody = true
      }
      
      // Reset video element
      this.popupVideo.pause()
      this.popupVideo.src = ''
      this.popupVideo.removeAttribute('src')
      
      // Clear existing source elements
      const existingSources = this.popupVideo.querySelectorAll('source')
      existingSources.forEach(source => source.remove())
      
      // If we have the original video element, copy all its source elements
      if (originalVideoElement) {
        const sourceElements = originalVideoElement.querySelectorAll('source')
        if (sourceElements.length > 0) {
          // Copy all source elements
          sourceElements.forEach(source => {
            const newSource = document.createElement('source')
            newSource.src = source.src
            newSource.type = source.type
            this.popupVideo.appendChild(newSource)
          })
        } else if (originalVideoElement.src) {
          // Fallback to video src if no source elements
          this.popupVideo.src = originalVideoElement.src
        }
      } else if (videoSrc) {
        // Use provided video source
        this.popupVideo.src = videoSrc
      }
      
      // Set video properties
      this.popupVideo.muted = true // Start muted
      this.popupVideo.loop = true // Enable looping
      this.popupVideo.load() // Force reload with new source
      
      if (this.muteButton) {
        this.muteButton.classList.add('muted')
      }
      if (this.playPauseButton) {
        this.playPauseButton.classList.add('playing')
      }
      
      // Prevent body scroll when popup is open
      document.body.style.overflow = 'hidden'
      
      this.popup.style.display = 'flex'
      // Use setTimeout to ensure display change is applied before adding active class
      setTimeout(() => {
        this.popup.classList.add('active')
      }, 10)
      
      // Wait for video to be ready before playing
      const playVideo = () => {
        this.popupVideo.play().catch(err => {
          console.log('Video autoplay prevented:', err)
          if (this.playPauseButton) {
            this.playPauseButton.classList.remove('playing')
          }
        })
      }
      
      // Handle video errors
      const onError = () => {
        console.error('Video failed to load')
        // Try to reload
        this.popupVideo.load()
      }
      this.popupVideo.addEventListener('error', onError, { once: true })
      
      // Try to play when video can play
      if (this.popupVideo.readyState >= 2) {
        // Video is already loaded
        playVideo()
      } else {
        // Wait for video to load
        const onCanPlay = () => {
          playVideo()
          this.popupVideo.removeEventListener('canplay', onCanPlay)
          this.popupVideo.removeEventListener('loadeddata', onCanPlay)
          this.popupVideo.removeEventListener('loadedmetadata', onCanPlay)
        }
        this.popupVideo.addEventListener('canplay', onCanPlay)
        this.popupVideo.addEventListener('loadeddata', onCanPlay)
        this.popupVideo.addEventListener('loadedmetadata', onCanPlay)
        
        // Fallback timeout - try to play even if events don't fire
        setTimeout(() => {
          this.popupVideo.removeEventListener('canplay', onCanPlay)
          this.popupVideo.removeEventListener('loadeddata', onCanPlay)
          this.popupVideo.removeEventListener('loadedmetadata', onCanPlay)
          // Only try to play if video has some data loaded
          if (this.popupVideo.readyState > 0) {
            playVideo()
          }
        }, 3000)
      }
    }
  }
  
  toggleMute() {
    if (this.popupVideo && this.muteButton) {
      this.popupVideo.muted = !this.popupVideo.muted
      if (this.popupVideo.muted) {
        this.muteButton.classList.add('muted')
      } else {
        this.muteButton.classList.remove('muted')
      }
    }
  }
  
  togglePlayPause() {
    if (this.popupVideo && this.playPauseButton) {
      if (this.popupVideo.paused) {
        this.popupVideo.play()
        this.playPauseButton.classList.add('playing')
      } else {
        this.popupVideo.pause()
        this.playPauseButton.classList.remove('playing')
      }
    }
  }
  
  closePopup() {
    if (this.popup && this.popupVideo) {
      this.popup.classList.remove('active')
      // Restore body scroll
      document.body.style.overflow = ''
      // Wait for transition to complete before hiding
      setTimeout(() => {
        this.popup.style.display = 'none'
        this.popupVideo.pause()
        this.popupVideo.src = ''
      }, 300)
    }
  }
  
  disconnectedCallback() {
    // Clean up event listeners if needed
  }
}

customElements.define('image-video', ImageVideo)

